"use client";

import { v4 as uuidv4 } from "uuid";
import { supabase } from "./supabase";
import { ItemImage } from "./types";
import type { Database, Json } from "./database.types";

// Importing browser image compression library
// npm install browser-image-compression --save
import imageCompression from "browser-image-compression";
import logger from "./utils/logger";
import {
  buildListingImageSignature,
  computeListingImageContentHashFromBlob,
} from "@/lib/listing/listingPhotoDuplicateCheck";
import {
  buildItemImageContentHashOrFilter,
  scaleImageDimensions,
} from "@/lib/listing/listingImageMasterFormat";

// Types for image operations
type ItemImagesRow = Database["public"]["Tables"]["item_images"]["Row"];
type ItemImagesInsert = Database["public"]["Tables"]["item_images"]["Insert"];
type ItemImagesUpdate = Database["public"]["Tables"]["item_images"]["Update"];

export type ImageMetadata = {
  hash?: string;
  signature?: string;
  originalName?: string;
  originalSize?: number;
  originalType?: string;
  uploadedBy?: string;
  uploadTime?: string;
  source?: "local" | "remote";
  compressedSize?: number;
  compressedType?: string;
  fileKey?: string;
} & {
  [key: string]: Json | undefined;
};

export interface ImageUploadOptions {
  file: File;
  itemId: string;
  caption?: string;
  isMain?: boolean;
  metadata?: ImageMetadata;
  /**
   * When true, `file` is already compressed client-side (e.g. ImageUploader preview path).
   * Avoids a second compress pass inside uploadItemImage.
   */
  skipCompression?: boolean;
}

export interface ImageUpdateOptions {
  imageId: string;
  itemId: string;
  file?: File;
  caption?: string;
  isMain?: boolean;
  position?: number;
  metadata?: ImageMetadata;
}

interface ImageHash {
  hash: string;
  signature: string;
}

// Detect WebP canvas-encode capability once at module load.
// Safari on iOS cannot encode WebP from a canvas — we fall back to JPEG there.
let _webpEncodeCapable: boolean | null = null;
function isWebpEncodeCapable(): boolean {
  if (_webpEncodeCapable !== null) return _webpEncodeCapable;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    _webpEncodeCapable = canvas
      .toDataURL("image/webp")
      .startsWith("data:image/webp");
  } catch {
    _webpEncodeCapable = false;
  }
  return _webpEncodeCapable;
}

/**
 * Force-converts a file to WebP (or JPEG on browsers that can't encode WebP)
 * via a canvas draw. Used when browser-image-compression short-circuits and
 * returns the original format (e.g. source already under maxSizeMB).
 * Also enforces maxWidthOrHeight so oversized-under-maxSizeMB masters are scaled.
 */
async function forceWebpOrJpegConversion(
  file: File,
  quality: number,
  maxWidthOrHeight: number,
): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { width, height } = scaleImageDimensions(
        img.naturalWidth,
        img.naturalHeight,
        maxWidthOrHeight,
      );
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      const targetMime = isWebpEncodeCapable() ? "image/webp" : "image/jpeg";
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const ext = targetMime === "image/webp" ? "webp" : "jpg";
          resolve(
            new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.${ext}`, {
              type: targetMime,
            }),
          );
        },
        targetMime,
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    img.src = objectUrl;
  });
}

// Image compression settings cache
let compressionSettingsCache: {
  high_quality: number;
  high_max_width: number;
  medium_quality: number;
  medium_max_width: number;
  low_quality: number;
  low_max_width: number;
  avatar_quality: number;
  avatar_max_width: number;
  expiry: number;
} | null = null;

const COMPRESSION_SETTINGS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches image compression settings from system settings
 * Returns null if settings can't be fetched (will use defaults)
 */
async function getCompressionSettings(): Promise<{
  high_quality: number;
  high_max_width: number;
  medium_quality: number;
  medium_max_width: number;
  low_quality: number;
  low_max_width: number;
  avatar_quality: number;
  avatar_max_width: number;
} | null> {
  const now = Date.now();

  // Return cached settings if still valid
  if (compressionSettingsCache && now < compressionSettingsCache.expiry) {
    return compressionSettingsCache;
  }

  try {
    const response = await fetch("/api/settings/system", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      logger.warn(
        "[IMAGE_UTILS] Failed to fetch compression settings, using defaults",
      );
      return null;
    }

    const data = await response.json();
    const settings = data.settings || {};

    // Extract compression settings with defaults
    const compressionSettings = {
      high_quality: settings.image_compression_high_quality ?? 0.78,
      high_max_width: settings.image_compression_high_max_width ?? 1920,
      medium_quality: settings.image_compression_medium_quality ?? 0.5,
      medium_max_width: settings.image_compression_medium_max_width ?? 1200,
      low_quality: settings.image_compression_low_quality ?? 0.3,
      low_max_width: settings.image_compression_low_max_width ?? 1400,
      avatar_quality: settings.image_compression_avatar_quality ?? 0.7,
      avatar_max_width: settings.image_compression_avatar_max_width ?? 400,
      expiry: now + COMPRESSION_SETTINGS_CACHE_DURATION,
    };

    compressionSettingsCache = compressionSettings;
    return compressionSettings;
  } catch (error) {
    logger.warn("[IMAGE_UTILS] Error fetching compression settings:", error);
    return null;
  }
}

// Image compression options with different levels
// These are defaults - can be overridden by system settings (Admin → Settings).
/** All listing photos (main + gallery) use the high tier — see LISTING_IMAGE_COMPRESSION_LEVEL. */
export const LISTING_IMAGE_COMPRESSION_LEVEL = "high" as const;

const compressionOptions = {
  high: {
    maxSizeMB: 1.2,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    fileType: "image/webp",
    quality: 0.78,
  },
  medium: {
    maxSizeMB: 0.8,
    maxWidthOrHeight: 1200, // Reduced from 1400
    useWebWorker: true,
    fileType: "image/webp",
    quality: 0.5, // Reduced from 0.55
  },
  low: {
    maxSizeMB: 1.5,
    maxWidthOrHeight: 1400, // Reduced from 1600
    useWebWorker: true,
    fileType: "image/webp",
    quality: 0.3, // Reduced from 0.35
  },
  avatar: {
    maxSizeMB: 0.2,
    maxWidthOrHeight: 400,
    useWebWorker: true,
    fileType: "image/webp",
    quality: 0.7, // Reduced from 0.75
  },
};

/**
 * Simple hash function that works in all contexts
 */
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toImageDimensions = (
  value: Json | null,
): { width: number; height: number } | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const width = typeof record.width === "number" ? record.width : undefined;
  const height = typeof record.height === "number" ? record.height : undefined;

  if (typeof width === "number" && typeof height === "number") {
    return { width, height };
  }

  return undefined;
};

const toImageMetadata = (value: Json | null): ImageMetadata | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  return value as ImageMetadata;
};

const mapRowToItemImage = (row: ItemImagesRow): ItemImage => ({
  id: row.id,
  itemId: row.item_id,
  url: row.url,
  position: row.position ?? 0,
  caption: row.caption ?? undefined,
  dimensions: toImageDimensions(row.dimensions),
  fileSize: row.file_size ?? undefined,
  fileType: row.file_type ?? undefined,
  isMain: Boolean(row.is_main),
  metadata: toImageMetadata(row.metadata),
  createdAt: row.created_at ? new Date(row.created_at) : undefined,
  hash: row.image_hash ?? undefined,
  signature: row.image_signature ?? undefined,
});

const blurCache = new Map<string, string>();

const bufferToBase64 = (buffer: ArrayBuffer): string => {
  if (typeof window === "undefined") {
    return Buffer.from(buffer).toString("base64");
  }

  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
  }
  return window.btoa(binary);
};

/**
 * Generates a hash for an image file
 */
export const generateImageHash = async (file: File): Promise<ImageHash> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        let hash: string;
        let signature: string;
        let method: "crypto" | "fallback" = "fallback";

        // Check for secure context and crypto support
        const isSecureContext = window.isSecureContext;
        const hasCryptoSupport =
          typeof window !== "undefined" &&
          window.crypto &&
          window.crypto.subtle;

        logger.log("Image hash generation context:", {
          isSecureContext,
          hasCryptoSupport,
          fileType: file.type,
          fileSize: file.size,
        });

        if (isSecureContext && hasCryptoSupport) {
          try {
            method = "crypto";
            const arrayBuffer = e.target?.result as ArrayBuffer;
            const hashBuffer = await crypto.subtle.digest(
              "SHA-256",
              arrayBuffer,
            );
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            hash = hashArray
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");
            signature = hashArray.slice(0, 8).join("");
          } catch (cryptoError) {
            logger.warn(
              "Crypto API failed, falling back to simple hash:",
              cryptoError,
            );
            method = "fallback";
            const content = e.target?.result as string;
            hash = simpleHash(content);
            signature = hash.slice(0, 16);
          }
        } else {
          const content = e.target?.result as string;
          hash = simpleHash(content);
          signature = hash.slice(0, 16);
        }

        logger.log("Image hash generated successfully:", {
          method,
          hashLength: hash.length,
          signatureLength: signature.length,
        });

        resolve({ hash, signature });
      } catch (error) {
        logger.error("Error in image hash generation:", {
          error,
          fileInfo: {
            name: file.name,
            type: file.type,
            size: file.size,
          },
        });
        // Always ensure we return a hash, even in error cases
        const fallbackContent = `${file.name}-${file.size}-${file.lastModified}`;
        const fallbackHash = simpleHash(fallbackContent);
        resolve({
          hash: fallbackHash,
          signature: fallbackHash.slice(0, 16),
        });
      }
    };

    reader.onerror = (error) => {
      logger.error("FileReader error:", error);
      // Even in case of read error, provide a hash
      const fallbackContent = `${file.name}-${file.size}-${file.lastModified}`;
      const fallbackHash = simpleHash(fallbackContent);
      resolve({
        hash: fallbackHash,
        signature: fallbackHash.slice(0, 16),
      });
    };

    // Try ArrayBuffer first, fall back to text if it fails
    try {
      reader.readAsArrayBuffer(file);
    } catch (error) {
      logger.warn("ArrayBuffer read failed, falling back to text:", error);
      reader.readAsText(file);
    }
  });
};

/**
 * Checks if an image already exists in the database
 */
export const checkImageExists = async (
  hash: string,
  signature: string,
): Promise<boolean> => {
  const orFilter = buildItemImageContentHashOrFilter(hash, signature);
  if (!orFilter) return false;

  const { data, error } = await supabase
    .from("item_images")
    .select("id")
    .or(orFilter)
    .limit(1);

  if (error) {
    logger.warn("[IMAGE_UTILS] checkImageExists query failed:", error);
    return false;
  }

  return (data?.length ?? 0) > 0;
};

/**
 * Compresses an image file before upload
 * @param file The file to compress
 * @param compressionLevel The compression level to use (high, medium, low, avatar)
 * @param customOptions Custom compression options to override the defaults
 * @returns Compressed file
 */
export const compressImage = async (
  file: File,
  compressionLevel: "high" | "medium" | "low" | "avatar" = "medium",
  customOptions?: Partial<typeof compressionOptions.medium>,
): Promise<File> => {
  try {
    // Try to fetch compression settings from system settings
    const settings = await getCompressionSettings();

    // Select base options based on compression level
    const baseOptions = { ...compressionOptions[compressionLevel] };

    // Override with system settings if available
    if (settings) {
      switch (compressionLevel) {
        case "high":
          baseOptions.quality = settings.high_quality;
          baseOptions.maxWidthOrHeight = settings.high_max_width;
          break;
        case "medium":
          baseOptions.quality = settings.medium_quality;
          baseOptions.maxWidthOrHeight = settings.medium_max_width;
          break;
        case "low":
          baseOptions.quality = settings.low_quality;
          baseOptions.maxWidthOrHeight = settings.low_max_width;
          break;
        case "avatar":
          baseOptions.quality = settings.avatar_quality;
          baseOptions.maxWidthOrHeight = settings.avatar_max_width;
          break;
      }
    }

    // Merge with any custom options
    const options = customOptions
      ? { ...baseOptions, ...customOptions }
      : baseOptions;

    // Adaptive compression for large images
    if (file.size > 5 * 1024 * 1024) {
      // Over 5MB
      options.quality = Math.max(0.3, (options.quality || 0.6) - 0.2);
      options.maxWidthOrHeight = Math.min(
        1920,
        options.maxWidthOrHeight || 1920,
      );
      logger.log("Large image detected, using stronger compression");
    } else if (file.size > 2 * 1024 * 1024) {
      // 2-5MB
      options.quality = Math.max(0.4, (options.quality || 0.6) - 0.1);
      logger.log("Medium-large image detected, using stronger compression");
    }

    // Listing / avatar defaults target WebP (see compressionOptions). Do not
    // keep PNG masters — they dominate Free-plan Storage. Callers that need
    // PNG must pass customOptions.fileType explicitly.

    logger.log("Compressing image with options:", {
      fileName: file.name,
      originalSize: (file.size / 1024).toFixed(2) + "KB",
      fileType: file.type,
      compressionLevel,
      targetQuality: options.quality,
      maxWidthOrHeight: options.maxWidthOrHeight,
      targetFormat: options.fileType,
    });

    // Perform the compression
    let compressedFile = await imageCompression(file, options);

    // browser-image-compression short-circuits when source is already under
    // maxSizeMB and returns the original file, ignoring fileType / resize.
    // Force WebP (or JPEG on Safari) + max-edge scale whenever result isn't WebP.
    if (compressionLevel !== "avatar" && compressedFile.type !== "image/webp") {
      logger.log(
        "[compressImage] result not WebP after compress — forcing conversion + max edge",
        { originalType: compressedFile.type, size: compressedFile.size },
      );
      compressedFile = await forceWebpOrJpegConversion(
        compressedFile,
        options.quality ?? 0.78,
        options.maxWidthOrHeight ?? 1920,
      );
    }

    // Log compression results
    const compressionRatio = (file.size / compressedFile.size).toFixed(2);
    const savedSpace = ((file.size - compressedFile.size) / 1024).toFixed(2);

    logger.log("Compression complete:", {
      originalSize: (file.size / 1024).toFixed(2) + "KB",
      compressedSize: (compressedFile.size / 1024).toFixed(2) + "KB",
      compressionRatio: compressionRatio + "x",
      savedSpace: savedSpace + "KB",
      compressionPercent:
        ((1 - compressedFile.size / file.size) * 100).toFixed(0) + "%",
    });

    return compressedFile;
  } catch (error) {
    logger.error("Error compressing image:", error);
    // Return original file if compression fails
    return file;
  }
};

/**
 * Gets image dimensions
 */
export const getImageDimensions = (
  file: File,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      });
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
    };
    img.src = URL.createObjectURL(file);
  });
};

/** Listing upload path — all three item photos use high tier (Appendix A: 0.78 / 1920px). */
export const compressListingImage = (file: File): Promise<File> =>
  compressImage(file, LISTING_IMAGE_COMPRESSION_LEVEL);

/**
 * Uploads an image to the Supabase Storage bucket and adds an entry to the item_images table.
 * Single compress pass: callers that already ran compressListingImage set skipCompression.
 */
export const uploadItemImage = async (
  options: ImageUploadOptions,
): Promise<ItemImage | null> => {
  const {
    file,
    itemId,
    caption = "",
    isMain = false,
    metadata = {},
    skipCompression = false,
  } = options;
  logger.log("uploadItemImage called with file:", file.name);

  try {
    const compressedFile = skipCompression
      ? file
      : await compressImage(file, LISTING_IMAGE_COMPRESSION_LEVEL);
    if (!compressedFile) return null;

    if (skipCompression) {
      logger.log("uploadItemImage: using pre-compressed file (single pass)");
    } else {
      logger.log(
        `uploadItemImage: compressed with ${LISTING_IMAGE_COMPRESSION_LEVEL} tier`,
      );
    }

    if (compressedFile.type === "image/png") {
      logger.warn(
        "[IMAGE_UPLOAD] PNG master about to be stored — compressImage did not convert to WebP/JPEG. " +
          "Check forceWebpOrJpegConversion path (possible canvas failure).",
        { size: compressedFile.size, originalType: file.type },
      );
    }

    // Extension must match compressed payload (WebP), not the original upload name
    const fileExt =
      compressedFile.type === "image/webp"
        ? "webp"
        : compressedFile.type === "image/png"
          ? "png"
          : compressedFile.type === "image/jpeg"
            ? "jpg"
            : file.name.split(".").pop() || "webp";
    const fileName = `${itemId}_${uuidv4()}.${fileExt}`;

    // Upload the file directly to the bucket root for simplicity
    const { error: uploadError } = await supabase.storage
      .from("items")
      .upload(fileName, compressedFile, {
        cacheControl: "2592000",
        upsert: false,
        contentType: compressedFile.type,
      });

    if (uploadError) {
      logger.error("Error uploading image:", uploadError);
      return null;
    }

    // Get the public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from("items").getPublicUrl(fileName);

    // Get image dimensions
    const dimensions = await getImageDimensions(compressedFile);

    // If this is the main image, set position to 0
    // If not main, get the highest position number for this item and add 1
    let position = 0;

    if (!isMain) {
      const { data: existingImages, error: fetchError } = await supabase
        .from("item_images")
        .select("position")
        .eq("item_id", itemId)
        .order("position", { ascending: false })
        .limit(1);

      if (!fetchError && existingImages && existingImages.length > 0) {
        const latestPosition = existingImages[0].position ?? 0;
        position = latestPosition + 1;
      }
    } else {
      // If this is the main image, reorder existing images to make space for it at position 0
      const { data: existingImages, error: fetchError } = await supabase
        .from("item_images")
        .select("id, position")
        .eq("item_id", itemId)
        .order("position", { ascending: true });

      if (!fetchError && existingImages && existingImages.length > 0) {
        // Set all images as non-main
        await supabase
          .from("item_images")
          .update({ is_main: false })
          .eq("item_id", itemId);

        // Reorder existing images (push them down by 1)
        for (const img of existingImages) {
          const nextPosition = (img.position ?? 0) + 1;
          await supabase
            .from("item_images")
            .update({ position: nextPosition })
            .eq("id", img.id);
        }
      }
    }

    const contentHash =
      await computeListingImageContentHashFromBlob(compressedFile);
    const contentSignature = buildListingImageSignature(contentHash);

    // Store the image metadata
    const metadataPayload: ImageMetadata = {
      originalName: file.name,
      originalSize: file.size,
      originalType: file.type,
      uploadTime: new Date().toISOString(),
      hash: contentHash,
      signature: contentSignature,
      ...metadata,
    };

    const imageRecord: ItemImagesInsert = {
      item_id: itemId,
      url: publicUrl,
      position,
      caption: caption ?? null,
      dimensions: dimensions ?? null,
      file_size: compressedFile.size,
      file_type: compressedFile.type,
      is_main: isMain,
      image_hash: contentHash,
      image_signature: contentSignature,
      metadata: metadataPayload,
    };

    const { data: insertedImage, error: insertError } = await supabase
      .from("item_images")
      .insert([imageRecord])
      .select()
      .single<ItemImagesRow>();

    if (insertError) {
      logger.error("Error inserting image record:", insertError);
      // Clean up the uploaded file if database insert fails
      await supabase.storage.from("items").remove([fileName]);
      return null;
    }

    return mapRowToItemImage(insertedImage);
  } catch (error) {
    logger.error("Error uploading image:", error);
    return null;
  }
};

/**
 * Updates an existing image
 */
export const updateItemImage = async (
  options: ImageUpdateOptions,
): Promise<ItemImage | null> => {
  try {
    const { imageId, itemId, file, caption, isMain, position, metadata } =
      options;

    // Get the current image data
    const { data: currentImageData, error: fetchError } = await supabase
      .from("item_images")
      .select("*")
      .eq("id", imageId)
      .single<ItemImagesRow>();

    if (fetchError) {
      logger.error("Error fetching current image data:", fetchError);
      return null;
    }

    // Prepare update data
    const updateData: ItemImagesUpdate = {};

    // If we're uploading a new file
    if (file) {
      const compressedFile = await compressImage(
        file,
        LISTING_IMAGE_COMPRESSION_LEVEL,
      );

      // Get image dimensions
      const dimensions = await getImageDimensions(compressedFile);

      // Filename must match compressed payload (WebP), not the original name
      const fileExt =
        compressedFile.type === "image/webp"
          ? "webp"
          : compressedFile.type === "image/png"
            ? "png"
            : compressedFile.type === "image/jpeg"
              ? "jpg"
              : compressedFile.name.split(".").pop() || "webp";
      const fileName = `${uuidv4()}.${fileExt}`; // Remove itemId from filename for cleaner storage

      // Store directly in bucket root
      const filePath = fileName;

      // Extract the old file path from the URL
      const urlParts = currentImageData.url.split("/");
      const oldFilePath = urlParts[urlParts.length - 1]; // Just get the filename

      // Upload the new file to Supabase Storage
      const { error: storageError } = await supabase.storage
        .from("items")
        .upload(filePath, compressedFile, {
          cacheControl: "2592000",
          upsert: false,
        });

      if (storageError) {
        logger.error(
          "Error uploading replacement image to storage:",
          storageError,
        );
        return null;
      }

      // Get the public URL for the uploaded file
      const { data: urlData } = supabase.storage
        .from("items")
        .getPublicUrl(filePath);

      if (!urlData || !urlData.publicUrl) {
        logger.error("Error getting public URL for replacement image");
        return null;
      }

      const contentHash =
        await computeListingImageContentHashFromBlob(compressedFile);
      const contentSignature = buildListingImageSignature(contentHash);

      // Update the image data
      updateData.url = urlData.publicUrl;
      updateData.dimensions = dimensions;
      updateData.file_size = compressedFile.size;
      updateData.file_type = compressedFile.type;
      updateData.image_hash = contentHash;
      updateData.image_signature = contentSignature;

      // Remove the old file after successful upload and update
      await supabase.storage.from("items").remove([oldFilePath]);
    }

    // Update caption if provided
    if (caption !== undefined) {
      updateData.caption = caption;
    }

    // Update position if provided
    if (position !== undefined) {
      updateData.position = position;
    }

    // Update metadata if provided
    if (metadata !== undefined) {
      updateData.metadata = metadata;
    }

    // If this is set as the main image
    if (isMain) {
      // First, get all current images for the item to handle positions correctly
      const { data: otherImages, error: fetchOtherError } = await supabase
        .from("item_images")
        .select("id, position")
        .eq("item_id", itemId)
        .neq("id", imageId) // Get all except the current one
        .order("position", { ascending: true });

      if (fetchOtherError) {
        logger.error(
          "Error fetching other images for position update:",
          fetchOtherError,
        );
      }

      // Set all other images as non-main
      await supabase
        .from("item_images")
        .update({ is_main: false })
        .eq("item_id", itemId);

      updateData.is_main = true;
      updateData.position = 0; // Main image should always be at position 0

      // Reorder other images if needed
      if (!fetchOtherError && otherImages && otherImages.length > 0) {
        for (let i = 0; i < otherImages.length; i++) {
          await supabase
            .from("item_images")
            .update({ position: i + 1 }) // Start from position 1
            .eq("id", otherImages[i].id);
        }
      }
    }

    // Update the record
    const { data: updatedImageData, error: updateError } = await supabase
      .from("item_images")
      .update(updateData)
      .eq("id", imageId)
      .select()
      .single<ItemImagesRow>();

    if (updateError) {
      logger.error("Error updating image record:", updateError);
      return null;
    }

    return mapRowToItemImage(updatedImageData);
  } catch (error) {
    logger.error("Error in updateItemImage:", error);
    return null;
  }
};

/**
 * Deletes an image from storage and the database
 */
export const deleteItemImage = async (imageId: string): Promise<boolean> => {
  try {
    // Get the image data to get the file URL
    const { data: imageData, error: fetchError } = await supabase
      .from("item_images")
      .select("*")
      .eq("id", imageId)
      .single();

    if (fetchError) {
      logger.error("Error fetching image data for deletion:", fetchError);
      return false;
    }

    // Extract the filename from the URL
    const urlParts = imageData.url.split("/items/");
    if (urlParts.length !== 2) {
      logger.error("Invalid image URL format:", imageData.url);
      return false;
    }

    // Get just the filename without query parameters
    const fileName = urlParts[1].split("?")[0];

    // Delete from the Supabase Storage
    const { error: storageError } = await supabase.storage
      .from("items")
      .remove([fileName]);

    if (storageError) {
      logger.error("Error deleting image from storage:", storageError);
      return false;
    }

    // Delete from the database
    const { error: dbError } = await supabase
      .from("item_images")
      .delete()
      .eq("id", imageId);

    if (dbError) {
      logger.error("Error deleting image record from database:", dbError);
      return false;
    }

    // If the deleted image was the main image, set another one as main
    if (imageData.is_main) {
      const { data: remainingImages, error: remainingError } = await supabase
        .from("item_images")
        .select("id")
        .eq("item_id", imageData.item_id)
        .order("position", { ascending: true })
        .limit(1);

      if (!remainingError && remainingImages && remainingImages.length > 0) {
        await supabase
          .from("item_images")
          .update({ is_main: true })
          .eq("id", remainingImages[0].id);
      }
    }

    return true;
  } catch (error) {
    logger.error("Error in deleteItemImage:", error);
    return false;
  }
};

/**
 * Gets all images for an item
 */
export const getItemImages = async (itemId: string): Promise<ItemImage[]> => {
  try {
    const { data, error } = await supabase
      .from("item_images")
      .select("*")
      .eq("item_id", itemId)
      .order("position", { ascending: true })
      .returns<ItemImagesRow[]>();

    if (error) {
      logger.error("Error fetching item images:", error);
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    return data.map(mapRowToItemImage);
  } catch (error) {
    logger.error("Error in getItemImages:", error);
    return [];
  }
};

/**
 * Reorders images for an item
 */
export const reorderItemImages = async (
  itemId: string,
  imageIds: string[],
): Promise<boolean> => {
  try {
    // Create a transaction to update all positions
    const updates = imageIds.map((id, index) =>
      supabase
        .from("item_images")
        .update({ position: index })
        .eq("id", id)
        .eq("item_id", itemId),
    );

    // Execute all updates
    await Promise.all(updates);

    return true;
  } catch (error) {
    logger.error("Error reordering item images:", error);
    return false;
  }
};

/**
 * Sets an image as the main/cover image
 */
export const setMainImage = async (
  imageId: string,
  itemId: string,
): Promise<boolean> => {
  try {
    // Get all current images for the item to handle positions correctly
    const { data: existingImages, error: fetchError } = await supabase
      .from("item_images")
      .select("id, position")
      .eq("item_id", itemId)
      .order("position", { ascending: true });

    if (fetchError) {
      logger.error("Error fetching existing images:", fetchError);
      return false;
    }

    // First, set all images for this item as not main
    const { error: resetError } = await supabase
      .from("item_images")
      .update({ is_main: false })
      .eq("item_id", itemId);

    if (resetError) {
      logger.error("Error resetting main image status:", resetError);
      return false;
    }

    // Then, set the specified image as main and position to 0
    const { error: updateError } = await supabase
      .from("item_images")
      .update({ is_main: true, position: 0 })
      .eq("id", imageId)
      .eq("item_id", itemId);

    if (updateError) {
      logger.error("Error setting main image:", updateError);
      return false;
    }

    // Reorder other images (all non-main images must have position > 0)
    const otherImages = existingImages
      .filter((img) => img.id !== imageId)
      .map((img, index) => ({
        id: img.id,
        position: index + 1, // Start positions from 1 since main image is 0
      }));

    if (otherImages.length > 0) {
      // Update positions of other images
      for (const img of otherImages) {
        const { error } = await supabase
          .from("item_images")
          .update({ position: img.position })
          .eq("id", img.id);

        if (error) {
          logger.error(`Error updating position for image ${img.id}:`, error);
          // Continue with the others even if one fails
        }
      }
    }

    return true;
  } catch (error) {
    logger.error("Error in setMainImage:", error);
    return false;
  }
};

/**
 * Gets the main image for an item
 */
export const getMainImage = async (
  itemId: string,
): Promise<ItemImage | null> => {
  try {
    const { data, error } = await supabase
      .from("item_images")
      .select("*")
      .eq("item_id", itemId)
      .eq("is_main", true)
      .single<ItemImagesRow>();

    if (error) {
      logger.error("Error fetching main image:", error);
      return null;
    }

    if (!data) {
      return null;
    }

    return mapRowToItemImage(data);
  } catch (error) {
    logger.error("Error in getMainImage:", error);
    return null;
  }
};

/**
 * Checks if a user has permission to manage images for an item
 * - Admins can edit all images
 * - Item owners can edit their own item's images
 * - Users can manage images for temporary items they're creating
 */
export const canManageItemImages = async (
  userId: string,
  itemId: string,
): Promise<boolean> => {
  try {
    if (!userId || !itemId) {
      return false;
    }

    // Check if the user is an admin
    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("is_admin")
      .eq("id", userId)
      .single();

    if (profileError) {
      logger.error("Error checking user admin status:", profileError);
      return false;
    }

    // If user is admin, they can manage all images
    if (profileData.is_admin) {
      return true;
    }

    // Check if the item exists in the database
    const { data: itemData, error: itemError } = await supabase
      .from("items")
      .select("seller_id")
      .eq("id", itemId)
      .single();

    if (itemError) {
      // Supabase error handling - check for "not found" error
      // This might be a temporary item ID used during item creation
      if (
        itemError.message?.includes("Not Found") ||
        itemError.message?.includes("No rows found") ||
        itemError.code === "PGRST116" ||
        itemError.code === "PGRST204"
      ) {
        logger.log(
          "Item not found in database, likely a temporary ID during creation.",
        );

        // For temporary items during creation, allow the current user to manage
        // Check if any images already exist for this item ID
        const { error: imageError } = await supabase
          .from("item_images")
          .select("id")
          .eq("item_id", itemId)
          .limit(1);

        if (imageError) {
          logger.error("Error checking for existing images:", imageError);
        }

        // If this is a new item being created (no item record yet), allow the current user to manage it
        return true;
      }

      logger.error("Error checking item ownership:", itemError);
      return false;
    }

    // Return true if the user is the owner of the item
    return itemData.seller_id === userId;
  } catch (error) {
    logger.error("Error in canManageItemImages:", error);
    return false;
  }
};

interface BlurOptions {
  width?: number;
  quality?: number;
  fit?: "cover" | "contain";
}

/** Public Storage URLs use /object/public/; transforms must use /render/image/public/ (Supabase docs). */
const SUPABASE_OBJECT_PUBLIC = "/storage/v1/object/public/";
const SUPABASE_RENDER_PUBLIC = "/storage/v1/render/image/public/";

/**
 * Build a Supabase Storage image transformation URL. Uses the render endpoint and
 * `resize` (not `fit`) per Storage image transformation API.
 */
export function buildSupabaseTransformedUrl(
  src: string,
  options: {
    width: number;
    height?: number;
    quality?: number;
    format?: string;
    resize?: "cover" | "contain" | "fill";
  },
): string {
  if (!src || typeof src !== "string" || !src.includes("supabase.co")) {
    return src;
  }
  try {
    const url = new URL(src);
    if (url.pathname.includes(SUPABASE_OBJECT_PUBLIC)) {
      url.pathname = url.pathname.replace(
        SUPABASE_OBJECT_PUBLIC,
        SUPABASE_RENDER_PUBLIC,
      );
    }
    url.searchParams.delete("fit");
    url.searchParams.set(
      "width",
      String(Math.max(1, Math.round(options.width))),
    );
    if (options.height != null) {
      url.searchParams.set(
        "height",
        String(Math.max(1, Math.round(options.height))),
      );
    }
    if (options.quality != null) {
      url.searchParams.set("quality", String(options.quality));
    }
    if (options.format != null) {
      url.searchParams.set("format", options.format);
    }
    if (options.resize != null) {
      url.searchParams.set("resize", options.resize);
    }
    return url.toString();
  } catch {
    return src;
  }
}

export const generateBlurDataUrl = async (
  url: string,
  options: BlurOptions = {},
): Promise<string> => {
  if (!url) return "";

  const cacheKey = `${url}|${options.width ?? "auto"}|${options.quality ?? "auto"}|${options.fit ?? "cover"}`;
  if (blurCache.has(cacheKey)) {
    return blurCache.get(cacheKey) as string;
  }

  try {
    const width = options.width ?? 24;
    const heightPx = Math.max(1, Math.round(width));
    const requestUrl =
      url.includes("supabase.co") && !url.startsWith("blob:")
        ? buildSupabaseTransformedUrl(url, {
            width,
            height: heightPx,
            quality: options.quality ?? 20,
            format: "webp",
            resize: options.fit ?? "cover",
          })
        : url;

    const response = await fetch(requestUrl);
    const buffer = await response.arrayBuffer();
    const base64 = bufferToBase64(buffer);
    const contentType = response.headers.get("content-type") ?? "image/webp";
    const blurData = `data:${contentType};base64,${base64}`;
    blurCache.set(cacheKey, blurData);
    return blurData;
  } catch (error) {
    logger.error("Error generating blur data URL:", error);
    return "";
  }
};

/**
 * Cleans up unused files for an item
 */
export const cleanupUnusedFiles = async (itemId: string): Promise<void> => {
  try {
    // List all files in storage with this item's prefix
    const { data: files, error: listError } = await supabase.storage
      .from("items")
      .list();

    if (listError) {
      logger.error("Error listing files:", listError);
      return;
    }

    // Get all active image URLs for this item
    const { data: activeImages } = await supabase
      .from("item_images")
      .select("url")
      .eq("item_id", itemId);

    // Create a set of active filenames
    const activeFilenames = new Set(
      (activeImages || []).map((img) => {
        const urlParts = img.url.split("/");
        return urlParts[urlParts.length - 1].split("?")[0];
      }),
    );

    // Find files that start with itemId but aren't in active URLs
    const unusedFiles = (files || [])
      .filter((file) => file.name.startsWith(`${itemId}_`))
      .filter((file) => !activeFilenames.has(file.name));

    if (unusedFiles.length === 0) return;

    // Remove unused files
    const { error: deleteError } = await supabase.storage
      .from("items")
      .remove(unusedFiles.map((f) => f.name));

    if (deleteError) {
      logger.error("Error deleting unused files:", deleteError);
    } else {
      logger.log(
        `Cleaned up ${unusedFiles.length} unused files for item ${itemId}`,
      );
    }
  } catch (error) {
    logger.error("Error in cleanupUnusedFiles:", error);
  }
};

// This module exports all the necessary functions for managing images in your application
const imageUtils = {
  uploadItemImage,
  updateItemImage,
  deleteItemImage,
  getItemImages,
  reorderItemImages,
  setMainImage,
  getMainImage,
  canManageItemImages,
  compressImage,
  compressListingImage,
  LISTING_IMAGE_COMPRESSION_LEVEL,
  getImageDimensions,
  buildSupabaseTransformedUrl,
  generateBlurDataUrl,
  generateImageHash,
  checkImageExists,
  cleanupUnusedFiles,
};

export {
  validateListingImageFile,
  analyzeListingImageQuality,
  getListingImageDimensions,
} from "@/lib/listingImageValidation";
export { fetchListingPhotoCoachSettings } from "@/lib/listingPhotoCoachSettings";

export default imageUtils;
